import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Autocomplete, Box, Button, Paper, TextField, Typography} from '@mui/material'
import {DataGrid, GridCellParams, GridColDef, GridRowsProp} from '@mui/x-data-grid'
import {SaveInventoryItem, SaveInventoryRequestItem} from '../utils'
import excludedSound from '../../assets/sounds/excluded.mp3'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import {ExpectedNonSerials} from '../transferUtils'
import {CachedProduct} from '../productCache'
import {ItemStatus, NotificationSnackbar} from '../../common/ui'

interface NonSerialStepProps {
    products: CachedProduct[]
    nonSerialItems: SaveInventoryItem[]
    expectedNonSerialItems: ExpectedNonSerials[]
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
    loading: Record<string, boolean>
    setLoading: (loadingState: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void
    qtyChanged: boolean
    setQtyChanged: (value: boolean) => void
}

interface QueuedEdit {
    sku: string
    previousQty: number
    currentQty: number
}

const ReceiveNonSerials = ({
                               products,
                               nonSerialItems,
                               expectedNonSerialItems,
                               saveRequestItem,
                               loading,
                               setLoading,
                               qtyChanged,
                               setQtyChanged
                           }: NonSerialStepProps) => {
    const [error, setError] = useState<string | null>(null)
    const [quantities, setQuantities] = useState<{[sku: string]: number}>({})
    const [initialQuantities, setInitialQuantities] = useState<{[sku: string]: number}>({})
    const [savedItems, setSavedItems] = useState<{
        sku: string;
        quantity: number,
        showSaveIcon: boolean,
        showDeleteIcon: boolean,
        status: string | undefined,
        disabled: boolean,
        enteredAt: string
    }[]>([])
    const [qty, setQty] = useState<number | null>(null)
    const [selectedNonSerial, setSelectedNonSerial] =
        useState<CachedProduct | null>(null)
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [unExpectedQty, setUnExpectedQty] = useState<number | null>(null)
    const [pendingQty, setPendingQty] = useState<number | null>(null)
    const [activeSku, setActiveSku] = useState<string | null>(null)
    const [queuedEdits, setQueuedEdits] = useState<QueuedEdit[]>([])
    const inputRefs = useRef<{[sku: string]: HTMLInputElement | null}>({})
    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const nonSerialProducts = products.filter(p => !p.serialized)

    const playAudio = () => {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    const getModelNumber = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product ? product.model : 'Unknown Model'
    }

    const getLocation = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product && product.bin ? product.bin : 'N/A'
    }

    const itemInExpectedNonSerials = (sku: string) => {
        return expectedNonSerialItems.some(item => item.sku === sku)
    }

    const allNonSerialItems = useMemo(() => {
        const updatedExpectedItems = expectedNonSerialItems.map(expectedItem => {
            // Find the matching item in the nonSerialItems (savedItems)
            const nonSerialItem = nonSerialItems.find(item => item.sku === expectedItem.sku)
            if (nonSerialItem) {
                return {
                    ...expectedItem,
                    model_number: getModelNumber(nonSerialItem.sku) || 'Unknown Model',
                    location: getLocation(nonSerialItem.sku) || 'Unknown Location',
                    quantity: nonSerialItem.quantity,
                    enteredAt: nonSerialItem.enteredAt
                }
            }
            return {
                ...expectedItem,
                model_number: getModelNumber(expectedItem.sku) || 'Unknown Model',
                location: getLocation(expectedItem.sku) || 'Unknown Location'
            }
        })

        // Concatenate the updated expected items with the nonSerialItems that are not in expectedItems
        const nonMatchingNonSerialItems = nonSerialItems.filter(item =>
            !expectedNonSerialItems.some(expectedItem => expectedItem.sku === item.sku)
        )

        return updatedExpectedItems.concat(nonMatchingNonSerialItems.map(item => {
            // For non-matching items in nonSerialItems, add them to the final array
            return {
                sku: item.sku,
                model_number: getModelNumber(item.sku) || 'Unknown Model',
                location: getLocation(item.sku) || 'Unknown Location',
                quantity: item.quantity,
                enteredAt: item.enteredAt
            }
        }))
    }, [expectedNonSerialItems, nonSerialItems])

    useEffect(() => {
        const reportMap: {[key: string]: number} = {}
        for (const item of allNonSerialItems) {
            reportMap[item.sku] = item.quantity
        }

        const initialQuantities: {[key: string]: number} = {}
        for (const item of allNonSerialItems) {
            initialQuantities[item.sku] = reportMap[item.sku] || 0
        }

        setQuantities(initialQuantities)
        setInitialQuantities(initialQuantities)

        setSavedItems(allNonSerialItems.map((item) => {
            const initialQuantity = initialQuantities[item.sku] || 0
            const showSaveIcon = initialQuantity !== item.quantity || !reportMap[item.sku]

            return {
                sku: item.sku,
                quantity: item.quantity,
                model_number: getModelNumber(item.sku),
                location: getLocation(item.sku),
                showSaveIcon,
                showDeleteIcon: !showSaveIcon,
                status: item.progress,
                disabled: false,
                enteredAt: item.enteredAt ?? ''
            }
        }))
    }, [allNonSerialItems])

    useEffect(() => {
        let nonSerialQty = 0
        let unExpectedQty = 0
        let nonSerialPendingQty = 0
        for (let item of nonSerialItems) {
            let queuedEdit = queuedEdits.find(edit => edit.sku === item.sku)
            let expectedItem = expectedNonSerialItems.find(i => i.sku === item.sku)
            if (item.progress) {
                if (queuedEdit) {
                    nonSerialPendingQty += Math.abs(queuedEdit.currentQty - queuedEdit.previousQty)
                    nonSerialQty += item.quantity
                } else {
                    if (expectedItem) {
                        if (item.quantity > expectedItem.quantity)
                            unExpectedQty += item.quantity
                    } else {
                        unExpectedQty += item.quantity
                    }
                    nonSerialPendingQty += item.quantity
                }
                if (item.progress === 'q-delete' || item.progress === 'deleting') {
                    nonSerialQty += item.quantity
                }
            } else {
                if (queuedEdit) {
                    setQueuedEdits(prev => prev.filter(edit => edit.sku !== item.sku))
                }
                if (expectedItem) {
                    if (item.quantity > expectedItem.quantity)
                        unExpectedQty += item.quantity - expectedItem.quantity

                } else {
                    unExpectedQty += item.quantity
                }
                nonSerialQty += item.quantity
            }
        }
        setSavedQty(nonSerialQty)
        setUnExpectedQty(unExpectedQty)
        setPendingQty(nonSerialPendingQty)
    }, [nonSerialItems])

    const handleQuantityChange = (sku: string, newQuantity: number) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }
        const sanitizedValue = Math.max(newQuantity, 0)
        setQuantities((prev) => ({...prev, [sku]: sanitizedValue}))
        setQtyChanged(sanitizedValue !== initialQuantities[sku])

        setSavedItems((prev) => prev.map((item) =>
            item.sku === sku
                ? {...item, showSaveIcon: sanitizedValue !== initialQuantities[sku], showDeleteIcon: !activeSku}
                : item
        ))
    }

    const handleSaveItem = (sku: string) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }
        const quantity = quantities[sku] ?? 0
        setLoading((prev: {[sku: string]: boolean}) => ({...prev, [sku]: true}))

        if (quantity < 0) {
            setError('Quantity must be greater than or equal to 0.')
            setLoading((prev: {[sku: string]: boolean}) => ({...prev, [sku]: false}))
            return
        }

        try {
            const existingItem = savedItems.find((item) => item.sku === sku)
            //check to see if item in expected but not in nonSerialItems list
            // i.e its expected but not yet counted/saved then its new and operation is add
            const inExpectedItems = expectedNonSerialItems.some((item) => item.sku === sku)
            const inReportItems = nonSerialItems.some((item) => item.sku === sku)
            const newExpectedToSave = inExpectedItems && !inReportItems
            if (existingItem && !newExpectedToSave) {
                const updatedItem = {...existingItem, quantity}

                setSavedItems((prev) => prev.map((item) => (item.sku === sku ? updatedItem : item)))

                saveRequestItem({
                    op: 'update',
                    sku: sku,
                    quantity,
                    serial: null,
                    bin: null,
                    enteredAt: updatedItem.enteredAt
                })

                setQueuedEdits(prev => {
                    const existingEditIndex = prev.findIndex(item => item.sku === existingItem.sku)
                    if (existingEditIndex !== -1) {
                        const updatedEdits = [...prev]
                        updatedEdits[existingEditIndex] = {
                            sku: existingItem.sku,
                            previousQty: existingItem?.quantity || 0,
                            currentQty: quantity
                        }
                        return updatedEdits
                    } else {
                        return [
                            ...prev,
                            {
                                sku: existingItem.sku,
                                previousQty: existingItem?.quantity || 0,
                                currentQty: quantity
                            }
                        ]
                    }
                })
            } else {
                const newItem = {
                    sku,
                    quantity,
                    showSaveIcon: false,
                    showDeleteIcon: true,
                    status: '',
                    disabled: false,
                    enteredAt: new Date().toISOString()
                }

                setSavedItems((prev) => [...prev, newItem])

                saveRequestItem({
                    op: 'add',
                    sku: sku,
                    quantity,
                    serial: null,
                    bin: null,
                    enteredAt: newItem.enteredAt
                })
            }
            setInitialQuantities((prev) => ({...prev, [sku]: quantity}))
            setSavedItems((prev) =>
                prev.map((item) =>
                    item.sku === sku
                        ? {
                            ...item,
                            showSaveIcon: false,
                            showDeleteIcon: !item.showSaveIcon,
                            status: '',
                            disabled: false
                        }
                        : {...item, disabled: false}
                )
            )
            setLoading((prev) => ({...prev, [sku]: false}))
            setActiveSku(null)
            setQtyChanged(false)
        } catch (error) {
            console.error(error)
        }
    }

    const handleFocus = (sku: string) => {
        setActiveSku(sku)
    }

    const handleAddNonSerial = async () => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }

        if (selectedNonSerial && qty && qty > 0) {
            saveRequestItem({
                op: 'add',
                sku: selectedNonSerial.sku,
                quantity: qty,
                bin: null,
                serial: null,
                enteredAt: new Date().toISOString()
            })

            setQty(null)
            setSelectedNonSerial(null)
        }
    }

    const handleRemoveNonSerial = (sku: string) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }

        let itemToRemove = allNonSerialItems.find(item => item.sku === sku)

        if (itemToRemove) {
            saveRequestItem({
                op: 'delete',
                sku: itemToRemove.sku,
                quantity: itemToRemove.quantity,
                serial: null,
                bin: null,
                enteredAt: itemToRemove.enteredAt ?? ''
            })
        }

    }

    const showDeleteIconForExpectedItems = (item: any) => {
        if (itemInExpectedNonSerials(item.sku)) return false
        else {
            return item.sku !== activeSku
        }
    }

    const getRowStyle = (row: any) => {
        const overflowAmt = row.received - row.expected
        if (overflowAmt > 0) {
            return '#ff7f00'
        }
        if (overflowAmt < 0) {
            return '#cc0000'
        }
        return '#000000'
    }

    const rows: GridRowsProp = useMemo(() => {
        return allNonSerialItems.map((item) => {
            let reportItem = nonSerialItems.find(reportItem => reportItem.sku === item.sku)
            let expectedItem = expectedNonSerialItems.find(i => i.sku === item.sku)
            let quantity = quantities[item.sku] || 0

            return {
                id: item.sku,
                sku: item.sku,
                modelNumber: item.model_number || '',
                location: item.location,
                expected: expectedItem ? expectedItem.quantity : 0,
                received: quantity,
                status: reportItem?.progress,
                showSaveIcon:
                    item.sku === activeSku &&
                    savedItems.some(saved => saved.sku === item.sku && saved.showSaveIcon) ||
                    !nonSerialItems.find(reportItem => reportItem.sku === item.sku),
                showDeleteIcon: showDeleteIconForExpectedItems(item),
                disabled: qtyChanged && item.sku !== activeSku
            }
        })
    }, [allNonSerialItems, nonSerialItems, quantities, savedItems, activeSku, showDeleteIconForExpectedItems])

    const columns: GridColDef[] = [
        {
            field: 'sku',
            headerName: 'Item',
            flex: 1,
            renderCell: (params: GridCellParams) => (
                <div style={{
                    color: getRowStyle(params.row),
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%'
                }}>{params.row.sku}</div>
            )
        },
        {
            field: 'modelNumber',
            headerName: 'Model',
            flex: 1,
            renderCell: (params: GridCellParams) => (
                <div style={{
                    color: getRowStyle(params.row),
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%'
                }}>{params.row.modelNumber}</div>
            )
        },
        {
            field: 'location',
            headerName: 'Location',
            flex: 1,
            renderCell: (params: GridCellParams) => (
                <div style={{
                    color: getRowStyle(params.row),
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%'
                }}>{params.row.location}</div>
            )
        },
        {
            field: 'expected',
            headerName: 'Expected',
            flex: 1,
            renderCell: (params: GridCellParams) => (
                <div style={{
                    color: getRowStyle(params.row),
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%'
                }}>{params.row.expected === 0 ? 'unexpected' : params.row.expected}</div>
            )
        },
        {
            field: 'received',
            headerName: 'Received',
            flex: 1,
            renderCell: (params) => {
                inputRefs.current[params.row.sku] = document.createElement('input')
                let queuedEdit = queuedEdits.find(item => item.sku === params.row.sku)
                return (
                    <TextField
                        inputRef={(ref) => (inputRefs.current[params.row.sku] = ref)}
                        type="number"
                        value={queuedEdit ? queuedEdit.currentQty : params.row.received !== undefined ? params.row.received : 0}
                        onChange={(e) => handleQuantityChange(params.row.sku, Number(e.target.value))}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                            inputProps: {min: 0},
                            readOnly: params.row.disabled,
                            style: {
                                color: getRowStyle(params.row),
                                borderColor: getRowStyle(params.row)
                            }
                        }}
                        onFocus={() => handleFocus(params.row.sku)}
                        // onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, params.row.sku, rows.indexOf(params.row))}
                        disabled={params.row.disabled}
                    />
                )
            }
        },
        {
            field: 'status', headerName: 'Status', flex: 1,
            renderCell: (params) => {
                return !params.row.showSaveIcon && (
                    <ItemStatus progress={params.row.status} />
                )
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.5,
            renderCell: (params) => (
                params.row.showSaveIcon ? (
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: 'primary',
                                '&:hover': {
                                    backgroundColor: 'primary.dark'
                                }
                            }}
                            onClick={() => handleSaveItem(params.row.sku)}
                            disabled={params.row.disabled}
                        >
                            <SaveIcon sx={{color: 'white'}} />
                        </Button>
                    </Box>
                ) : showDeleteIconForExpectedItems(params.row) && (
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            sx={{
                                // backgroundColor: 'primary',
                                '&:hover': {
                                    backgroundColor: 'primary.dark'
                                }
                            }}
                            onClick={() => handleRemoveNonSerial(params.row.sku)}
                            disabled={params.row.disabled}
                        >
                            <DeleteIcon />
                        </Button>
                    </Box>
                )
            )
        }
    ]

    return (
        <Box flex={1} p={2} border={2} borderColor="grey.400" borderRadius={2}>
            <Typography variant="h5">Enter Non-Serials</Typography>
            <Typography variant="subtitle1" color="textSecondary">
                <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                {savedQty} quantity,{' '}
                <span
                    style={{color: '#ff7f00'}}> {unExpectedQty && unExpectedQty > 0 ? `${unExpectedQty} unexpected` : `0 unexpected`},{' '} </span>
                <span style={{fontWeight: 700}}>Pending: </span>
                {pendingQty} quantity
            </Typography>
            <audio ref={audioPlayer} src={excludedSound} />
            <div className="my-3 grid max-w-5xl md:grid-cols-5 gap-y-4 md:gap-y-0">
                <div className="grid gap-1 pr-4 sm:pt-4 md:pt-0 sm:w-full col-span-2">
                    <Autocomplete
                        disablePortal
                        id="sampleSelection"
                        options={nonSerialProducts}
                        getOptionLabel={option =>
                            `${option.sku} - ${option.model || ''}`
                        }
                        value={selectedNonSerial}
                        onChange={(_e, newValue) =>
                            setSelectedNonSerial(newValue)
                        }
                        renderInput={params => (
                            <TextField
                                required
                                {...params}
                                label="Select item"
                            />
                        )}
                    />
                </div>
                <div className="grid gap-1 pr-4 md:pt-0 col-span-2 md:mt-0 mt-4">
                    <TextField
                        required
                        type="number"
                        placeholder="Quantity"
                        value={qty ?? ''}
                        onChange={e => setQty(Number(e.target.value))}
                        // onKeyDown={handleKeyDown}
                        className="flex"
                        label="Quantity"
                    />
                </div>
                <div className="grid gap-1 pr-4 sm:pt-4 md:pt-0 sm:w-full">
                    <Button
                        variant="contained"
                        className="text-right max-w-[100px]"
                        onClick={handleAddNonSerial}
                        disabled={window.sessionStorage.getItem('blocking') === 'true'}>
                        Add
                    </Button>
                </div>
            </div>
            {allNonSerialItems.length > 0 ? (
                <Paper style={{height: 400}}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowHeight={() => 'auto'}
                        disableRowSelectionOnClick
                    />
                </Paper>
            ) : (
                <Typography variant="h6" color="error" align="center" style={{marginTop: '20px'}}>
                    No non-serial items expected.
                </Typography>
            )}

            {error && (
                <NotificationSnackbar
                    open={!!error}
                    message={error}
                    onClose={() => setError(null)}
                    severity="error"
                    autoHideDuration={6000}
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                />
            )}
        </Box>
    )
}

export default ReceiveNonSerials
